import { atom } from "jotai";

import type { RequestSpec } from "@moment/api-collab";
import { Logger } from "@moment/logging";
import type { ClientStoreV2 } from "@moment/reboot-differential";

import { writeRequestPageSnapshot } from "../diagnostics-snapshot";
import { $resolvedDocumentEnvironmentVars, $selectedEnvironmentName } from "../environments";
import { $documentStoreV2 } from "../reboot/document-store-v2.atom";
import { logRequestPageError } from "../runtime-error-log";
import { executeRequest } from "./execute-request";
import {
	type QueryBuilder,
	type QueryBuilderOptions,
	type QueryExecutorInput,
	type QueryHandle,
	createQueryBuilder as createQueryBuilderImpl,
	createQueryHandle as createQueryHandleImpl,
} from "./query-handle";
import { $queryState, type QueryStateEntry, classifyError } from "./query-state.atom";
import {
	$recordRequestPageHistorySnapshot,
	runRequestPageHistoryWriteBestEffort,
} from "./request-page-history.atom";
import type { HttpRequestPage, QueryResponse } from "./request-page.types";

// Re-export from query-handle.ts
export {
	createQueryBuilderImpl as createQueryBuilder,
	createQueryHandleImpl as createQueryHandle,
	type QueryBuilder,
	type QueryBuilderOptions,
	type QueryHandle,
};

const logger = new Logger("query-executor");

// =============================================================================
// Query Executor Core Types
// =============================================================================

export type QueryExecutorCoreInput = {
	key: string;
	request: RequestSpec;
	params: Record<string, string>;
	/** When provided, document-scoped env vars are merged on top of global vars. */
	documentId?: string;
	/** Page ID for error logging context. */
	pageId?: string;
	/** Page name for error logging context. */
	pageName?: string;
	/** Full request page snapshot so persisted history can restore editor state. */
	requestPage?: HttpRequestPage;
	/** Optional store v2 instance for store-backed SQLite queries. */
	storeV2?: ClientStoreV2;
};

export type { QueryExecutorInput };

// =============================================================================
// Query Executor Core
// =============================================================================

/**
 * Write-only atom that executes a request (HTTP or Postgres) and updates query state.
 *
 * This is the pure execution core:
 * - If a request is already in flight, returns its promise (deduplication)
 * - Sets query state to fetching
 * - Sends unresolved request + env vars + params to Rust
 * - Rust performs variable substitution and execution
 * - Sets query state with result
 * - Returns QueryStateEntry (callers derive QueryResult with refetch at handle layer)
 */
export const $queryExecutorCore = atom(
	null, // Write-only: no read value
	async (get, set, input: QueryExecutorCoreInput): Promise<QueryStateEntry> => {
		const { key, request, params, documentId, pageId, pageName, requestPage, storeV2 } = input;
		const stateAtom = $queryState(key);
		const currentState = get(stateAtom);

		// Deduplication: if there's already a pending request, await it
		if (currentState.pendingPromise) {
			logger.debug("Deduplicating request", { key });
			try {
				await currentState.pendingPromise;
			} catch {
				// The pending request failed, continue to return current state
			}
			return get(stateAtom);
		}

		// Create abort controller for this request
		const abortController = new AbortController();

		// Set pendingPromise IMMEDIATELY to prevent race condition where concurrent
		// requests both pass the deduplication check before either sets pendingPromise.
		const {
			promise: deferredPromise,
			resolve: resolveDeferred,
			reject: rejectDeferred,
		} = Promise.withResolvers<QueryResponse>();

		// Prevent unhandled rejection warnings - dedup path catches rejections
		deferredPromise.catch((e) => {
			logger.error("Unhandled error in query", { e });
		});

		set(stateAtom, {
			...currentState,
			fetchStatus: "fetching",
			lastAccessedAt: Date.now(),
			pendingPromise: deferredPromise,
			abortController,
		});

		try {
			const environmentName = await get($selectedEnvironmentName);

			// For HTTP/Postgres requests, env var resolution happens on the Rust side
			// from the selected environment passed into Rust. For JavaScript requests, we resolve env vars here
			// since substitution happens on the JS side.
			let envVars: Record<string, string> = {};
			if (request.type === "javascript" && documentId) {
				envVars = await get(
					$resolvedDocumentEnvironmentVars({ documentId, environmentName })
				);
			}

			// Resolve the store v2 for SQLite requests (store-backed CSV materialization)
			let resolvedStoreV2 = storeV2;
			if (!resolvedStoreV2 && request.type === "sqlite" && documentId) {
				resolvedStoreV2 = (await get($documentStoreV2(documentId))) ?? undefined;
			}

			const { response, timing } = await executeRequest(
				request,
				params,
				abortController,
				envVars,
				environmentName,
				documentId,
				resolvedStoreV2
			);
			const now = Date.now();

			// Check for HTTP-level errors (Postgres errors arrive as thrown exceptions from Rust)
			if (response.kind === "http" && !response.ok) {
				const bodyText =
					typeof response.parsedBody === "string"
						? response.parsedBody
						: JSON.stringify(response.parsedBody);
				const error = classifyError({
					message: `HTTP ${response.status}: ${response.statusText}`,
					status: response.status,
					responseBody: bodyText,
					authHint: response.auth_hint,
				});
				if (documentId) {
					logRequestPageError({
						documentId,
						pageId,
						pageName,
						error: { message: error.message, code: error.code },
					});
					writeRequestPageSnapshot({
						documentId,
						pageId,
						pageName,
						data: response,
						error,
						timing,
					});
					if (pageId && requestPage) {
						runRequestPageHistoryWriteBestEffort(
							() =>
								set($recordRequestPageHistorySnapshot, {
									documentId,
									pageId,
									requestPage,
									response,
									error,
									timing,
								}),
							{ documentId, pageId }
						);
					}
				}
				const newState: QueryStateEntry = {
					data: currentState.data, // Preserve stale data
					dataUpdatedAt: currentState.dataUpdatedAt,
					error,
					errorUpdatedAt: now,
					fetchStatus: "idle",
					lastAccessedAt: now,
					pendingPromise: undefined,
					abortController: undefined,
					timing: currentState.timing, // Preserve timing from last success
				};
				set(stateAtom, newState);
				rejectDeferred(error);
				return newState;
			}

			// Success (HTTP 2xx or Postgres result)
			if (documentId) {
				writeRequestPageSnapshot({
					documentId,
					pageId,
					pageName,
					data: response,
					timing,
				});
				if (pageId && requestPage) {
					runRequestPageHistoryWriteBestEffort(
						() =>
							set($recordRequestPageHistorySnapshot, {
								documentId,
								pageId,
								requestPage,
								response,
								timing,
							}),
						{ documentId, pageId }
					);
				}
			}
			const newState: QueryStateEntry = {
				data: response,
				dataUpdatedAt: now,
				error: undefined,
				errorUpdatedAt: undefined,
				fetchStatus: "idle",
				lastAccessedAt: now,
				pendingPromise: undefined,
				abortController: undefined,
				timing,
			};
			set(stateAtom, newState);
			resolveDeferred(response);
			return newState;
		} catch (err) {
			// Handle abort
			const isAbortError = err instanceof DOMException && err.name === "AbortError";
			const isTimeout = isAbortError && abortController.signal.reason === "timeout";

			if (isAbortError && !isTimeout) {
				// User-initiated abort - preserve existing data, clear error
				logger.debug("Request aborted", { key });
				if (documentId) {
					writeRequestPageSnapshot({
						documentId,
						pageId,
						pageName,
						data: currentState.data,
						timing: currentState.timing,
					});
				}
				const now = Date.now();
				const newState: QueryStateEntry = {
					data: currentState.data,
					dataUpdatedAt: currentState.dataUpdatedAt,
					error: undefined,
					errorUpdatedAt: undefined,
					fetchStatus: "idle",
					lastAccessedAt: now,
					pendingPromise: undefined,
					abortController: undefined,
					timing: currentState.timing,
				};
				set(stateAtom, newState);
				rejectDeferred(err);
				return newState;
			}

			// Timeout or other error
			const errorMessage = isTimeout
				? "Request timed out"
				: err instanceof Error
					? err.message
					: String(err);
			const error = classifyError({ message: errorMessage });
			if (documentId) {
				logRequestPageError({
					documentId,
					pageId,
					pageName,
					error: { message: error.message, code: error.code },
				});
				writeRequestPageSnapshot({
					documentId,
					pageId,
					pageName,
					error,
				});
				if (pageId && requestPage) {
					runRequestPageHistoryWriteBestEffort(
						() =>
							set($recordRequestPageHistorySnapshot, {
								documentId,
								pageId,
								requestPage,
								error,
							}),
						{ documentId, pageId }
					);
				}
			}
			const now = Date.now();
			const newState: QueryStateEntry = {
				data: currentState.data, // Preserve stale data
				dataUpdatedAt: currentState.dataUpdatedAt,
				error,
				errorUpdatedAt: now,
				fetchStatus: "idle",
				lastAccessedAt: now,
				pendingPromise: undefined,
				abortController: undefined,
				timing: currentState.timing, // Preserve timing from last success
			};
			set(stateAtom, newState);
			rejectDeferred(error);
			return newState;
		}
	}
);
$queryExecutorCore.debugLabel = "queryExecutorCore";
